import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { ExchangeFile, ExchangeFileStatus } from 'types/exchangeService';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';

export type ModalType = 'RESEND_FILE' | 'SHOW_ERROR';

export default class FileExchangeStore {
  rootStore: RootStore;
  loading = false;
  files: ExchangeFile[] = [];
  totalElements = 0;
  totalPages = 0;
  page = 1;
  sponsorId = '';
  searchName = '';
  isWarningModalOpen = false;
  modalType: ModalType | null = null;
  selectedFile: ExchangeFile | null = null;
  status: ExchangeFileStatus | string = '';
  date = {
    from: new Date().toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
  };

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  public setSponsorId(sponsorId: string): void {
    this.sponsorId = sponsorId;
  }

  public setStatus(status: ExchangeFileStatus | string): void {
    this.status = status;
  }

  public setSearchName(searchName: string): void {
    this.searchName = searchName;
  }

  public setPage(page: number): void {
    this.page = page;
  }

  public updateDate(from: string, to: string): void {
    this.date = { from, to };
  }

  public closeWarningModal(): void {
    this.modalType = null;
    this.isWarningModalOpen = false;
    this.selectedFile = null;
  }

  public openWarningModal(file: ExchangeFile, modalType: ModalType): void {
    this.modalType = modalType;
    this.isWarningModalOpen = true;
    this.selectedFile = file;
  }

  public async getFiles(): Promise<void> {
    this.setLoading(true);
    const result = await this.rootStore.exchangeStore.getFiles(
      this.date.from,
      this.date.to,
      this.sponsorId,
      this.status,
      this.searchName,
      this.page,
    );
    runInAction(() => {
      this.totalElements = result.totalElements;
      this.totalPages = result.totalPages;
      this.files = result.result;
      this.setLoading(false);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  public getStatusOptions(): OptionType[] {
    return [
      { value: '', label: 'All' },
      { value: 'PULLED', label: 'Pulled' },
      { value: 'SENT', label: 'Sent' },
      { value: 'FAILED', label: 'Failed' },
    ];
  }

  public onSearch(value: string): void {
    this.setSearchName(value);
    this.setPage(1);
  }

  public onReset(): void {
    this.setSearchName('');
    this.setPage(1);
  }

  // eslint-disable-next-line class-methods-use-this
  public downloadFile(id: string): void {
    const link = `https://${COMMON_URLS.api}/exchange/Prod/download/${id}`;
    downloadByUrl(link);
  }

  public async downloadAllFiles(): Promise<void> {
    const sponsorId = this.sponsorId || '';
    const { from, to } = toJS(this.date);
    const res = await this.rootStore.exchangeStore.generateZipFile(from, to, sponsorId, this.status, this.searchName);
    const link = res.data?.generateZipFile;
    if (link) {
      downloadByUrl(link);
    }
  }

  public async resendFile(fileId: string): Promise<void> {
    this.setLoading(true);
    await this.rootStore.exchangeStore
      .resendFile(fileId)
      .then((res) => {
        if (res.data?.resendFile?.id) {
          this.rootStore.filesTrackingController.showToast(
            'The file has been successfully resent.',
            ToastColorTypes.SUCCESS,
          );
        } else {
          this.rootStore.filesTrackingController.showToast(
            'An issue has occurred while attempting to resend the file. Please try again later.',
            ToastColorTypes.WARNING,
          );
        }
        this.setLoading(false);
      })
      .catch(() => {
        this.rootStore.filesTrackingController.showToast(
          'An issue has occurred while attempting to resend the file. Please try again later.',
          ToastColorTypes.WARNING,
        );
        this.setLoading(false);
      });
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
